import { AxiosInstance } from 'axios'
import { MOTUS_ENDPOINTS } from 'api/ApiRoutes'
import { MotusSSOResponse, RoadSmartVideo, SettingsResponse } from 'types/motus'
import { UserProfile } from 'types/user'
import { ID } from 'types/utility'

class MotusApi {
  constructor(private axiosInstance: AxiosInstance) {}

  getRefreshToken = async ({
    loginId,
    password
  }: {
    loginId: string
    password: string
  }) => {
    const params = new URLSearchParams()
    params.append('loginId', loginId)
    params.append('password', password)
    params.append('tokenType', 'refresh_token')

    const d = await this.axiosInstance.post<string>(
      MOTUS_ENDPOINTS.LOGIN,
      params,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json'
        }
      }
    )
    return d.data
  }

  getAccessToken = async ({ refreshToken }: { refreshToken: string }) => {
    const params = new URLSearchParams()
    params.append('tokenType', 'access_token')
    const d = await this.axiosInstance.post<string>(
      MOTUS_ENDPOINTS.LOGIN,
      params,
      {
        headers: {
          Authorization: `Token token=${refreshToken}`
        }
      }
    )
    return d.data
  }

  getUserSettings = async ({ accessToken }: { accessToken: string }) => {
    const date = new Date().toISOString().split('T')[0]
    const params = new URLSearchParams()
    params.append('startDate', date)
    params.append('endDate', date)
    const d = await this.axiosInstance.get<SettingsResponse>(
      MOTUS_ENDPOINTS.SETTINGS,
      {
        params,
        headers: {
          Authorization: `Token token=${accessToken}`
        }
      }
    )
    return d.data
  }

  getCurrentUser = async ({ accessToken }: { accessToken: string }) => {
    const d = await this.axiosInstance.get<UserProfile>(MOTUS_ENDPOINTS.USER, {
      headers: {
        Authorization: `Token token=${accessToken}`
      }
    })
    return d.data
  }

  getSsoRedirectUrl = async ({ email }: { email: string }) => {
    const params = new URLSearchParams()
    params.append('email', email)
    params.append('origin', 'reimburse')
    const d = await this.axiosInstance.get<MotusSSOResponse>('auth/sso', {
      baseURL: import.meta.env.VITE_APP_MOTUS_API_BASE_URL.replace(
        'translationlayerapi/v6/',
        'v1/'
      ),
      params
    })
    return d.data
  }

  requestPasswordReset = async (body: { email: string }) => {
    const d = await this.axiosInstance.post(
      MOTUS_ENDPOINTS.REQUEST_PASSWORD_RESETS,
      body
    )
    return d.data
  }

  getRoadSmartVideos = async () => {
    const d = await this.axiosInstance.get<{ videos: RoadSmartVideo[] }>(
      MOTUS_ENDPOINTS.ROADSMART_VIDEOS
    )
    return d.data
  }

  markVideoAs = async ({
    videoId,
    event
  }: {
    videoId: ID
    event: 'started' | 'complete'
  }) => {
    const _url =
      event === 'started'
        ? MOTUS_ENDPOINTS.ROADSMART_VIDEO_STARTED
        : MOTUS_ENDPOINTS.ROADSMART_VIDEO_COMPLETE

    const d = await this.axiosInstance.post(
      _url.addUrlParams({
        id: videoId
      }),
      {}
    )
    return d.data
  }
}

export default MotusApi
